import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSwitchBusiness } from "@/hooks/use-businesses";
import { useCurrentUser } from "@/hooks/use-current-user";

type BackupResetAction = "download" | "restore" | "reset";

/**
 * Returns a navigate-with-switch function: switch the active business
 * (no-op when already active) then route to /settings/backup-reset with
 * the given query action. Used by per-row dropdown menus on
 * Account → Businesses and Adva → Account → Businesses.
 */
export function useNavigateToBackupReset() {
  const navigate = useNavigate();
  const switchBusiness = useSwitchBusiness();
  const currentUser = useCurrentUser();

  return useCallback(
    async (businessId: string, action: BackupResetAction) => {
      const target = `/settings/backup-reset?action=${action}`;
      if (currentUser?.businessId === businessId) {
        navigate(target);
        return;
      }
      try {
        await switchBusiness.mutateAsync(businessId);
        navigate(target);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Switch failed";
        toast.error("Couldn't switch business", { description: msg });
      }
    },
    [navigate, switchBusiness, currentUser?.businessId],
  );
}
